import { useEffect, useState } from "react";
import { API, useCurrency, useAuth } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Search, Users, ArrowLeft, Wallet, ShoppingBag, CreditCard, Ban, Plus, Minus, Loader2, Phone, Mail, Calendar, Lock, Unlock, ArrowDownLeft, ArrowUpRight, X, MapPin, FileText, FileSpreadsheet, Eye } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import CustomerSegmentsEditor from "../../components/CustomerSegmentsEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCustomers() {
  const navigate = useNavigate();
  const { symbol } = useCurrency();
  const { refreshUser } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [walletFilter, setWalletFilter] = useState("all");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Action modals
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showCashoutModal, setShowCashoutModal] = useState(false);
  const [actionAmount, setActionAmount] = useState("");
  const [actionNote, setActionNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { fetchCustomers(); }, []);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API}/admin/customers`, { credentials: "include" });
      if (response.ok) setCustomers(await response.json());
    } catch (error) { console.error("Failed to fetch customers:", error); }
    finally { setLoading(false); }
  };

  const fetchCustomerDetail = async (userId) => {
    setDetailLoading(true);
    setActiveTab("overview");
    try {
      const response = await fetch(`${API}/admin/customers/${userId}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCustomerDetail(data);
        setSelectedCustomer(userId);
      }
    } catch (error) { console.error("Failed to fetch detail:", error); }
    finally { setDetailLoading(false); }
  };

  const [impConfirm, setImpConfirm] = useState(null); // { user_id, email, name }

  const handleImpersonate = (userId, email, name) => {
    setImpConfirm({ user_id: userId, email, name });
  };

  const confirmImpersonate = async () => {
    if (!impConfirm) return;
    const { user_id: userId, email } = impConfirm;
    setImpConfirm(null);
    try {
      const r = await fetch(`${API}/admin/impersonate/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ target_user_id: userId }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed to start session");
      try { window.sessionStorage.setItem("imp_token", data.imp_token); } catch { /* ignore */ }
      await refreshUser();
      toast.success(`Now viewing as ${data.target.email}`);
      navigate("/home", { replace: true });
    } catch (e) { toast.error(e.message); }
    void email;
  };

  const handleBlock = async (userId, block) => {
    try {
      const response = await fetch(`${API}/admin/customers/${userId}/wallet/block`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ blocked: block }),
      });
      if (!response.ok) throw new Error("Failed");
      toast.success(`Wallet ${block ? "blocked" : "unblocked"}`);
      fetchCustomers();
      if (selectedCustomer === userId) fetchCustomerDetail(userId);
    } catch (error) { toast.error("Failed to update wallet status"); }
  };

  const handleLoadCredits = async () => {
    if (!actionAmount || parseFloat(actionAmount) <= 0) { toast.error("Enter a valid amount"); return; }
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/customers/${selectedCustomer}/wallet/load`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ amount: parseFloat(actionAmount), note: actionNote || "Admin credit load" }),
      });
      if (!response.ok) throw new Error("Failed");
      const data = await response.json();
      toast.success(data.message);
      setShowLoadModal(false);
      setActionAmount(""); setActionNote("");
      fetchCustomers();
      fetchCustomerDetail(selectedCustomer);
    } catch (error) { toast.error("Failed to load credits"); }
    finally { setActionLoading(false); }
  };

  const handleCashout = async () => {
    if (!actionAmount || parseFloat(actionAmount) <= 0) { toast.error("Enter a valid amount"); return; }
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/customers/${selectedCustomer}/wallet/cashout`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ amount: parseFloat(actionAmount), note: actionNote || "Admin cashout" }),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || "Failed"); }
      const data = await response.json();
      toast.success(data.message);
      setShowCashoutModal(false);
      setActionAmount(""); setActionNote("");
      fetchCustomers();
      fetchCustomerDetail(selectedCustomer);
    } catch (error) { toast.error(error.message || "Failed to cashout"); }
    finally { setActionLoading(false); }
  };

  const filtered = customers.filter((c) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!(c.name || "").toLowerCase().includes(q) && !(c.email || "").toLowerCase().includes(q) && !(c.phone || "").toLowerCase().includes(q)) return false;
    }
    if (walletFilter === "blocked" && !c.wallet_blocked) return false;
    if (walletFilter === "active" && c.wallet_blocked) return false;
    if (walletFilter === "zero" && (c.wallet_balance || 0) > 0) return false;
    return true;
  });

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A";
  const formatDateTime = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "N/A";

  // Customer Detail View
  if (selectedCustomer && customerDetail) {
    const c = customerDetail.customer;
    const stats = customerDetail.stats;
    return (
      <AdminLayout>
        <div className="animate-fade-in" data-testid="customer-detail">
          {/* Back + Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="sm" onClick={() => { setSelectedCustomer(null); setCustomerDetail(null); }}>
              <ArrowLeft size={18} />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">{c.name}</h1>
              <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                <span className="flex items-center gap-1"><Mail size={12} /> {c.email}</span>
                {c.phone && <span className="flex items-center gap-1"><Phone size={12} /> {c.phone}</span>}
                <span className="flex items-center gap-1"><Calendar size={12} /> Joined {formatDate(c.created_at)}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className={c.wallet_blocked ? "border-green-300 text-green-600" : "border-red-300 text-red-600"} onClick={() => handleBlock(c.user_id, !c.wallet_blocked)} data-testid="toggle-block-btn">
                {c.wallet_blocked ? <><Unlock size={16} className="mr-2" /> Unblock</> : <><Lock size={16} className="mr-2" /> Block</>}
              </Button>
            </div>
          </div>

          {/* Wallet Card + Actions */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className={`bg-white rounded-2xl border-2 p-5 ${c.wallet_blocked ? "border-red-200 bg-red-50" : "border-green-200"}`}>
              <p className="text-sm text-gray-500 mb-1">Wallet Balance</p>
              <p className="text-3xl font-bold text-gray-900" data-testid="customer-balance">{symbol}{Math.round(c.wallet_balance || 0)}</p>
              {c.wallet_blocked && <span className="inline-flex items-center gap-1 text-xs text-red-600 mt-2 bg-red-100 px-2 py-0.5 rounded-full"><Ban size={10} /> Blocked</span>}
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <p className="text-sm text-gray-500 mb-1">Orders</p>
              <p className="text-3xl font-bold text-gray-900">{stats.order_count}</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <p className="text-sm text-gray-500 mb-1">Total Spent</p>
              <p className="text-3xl font-bold text-gray-900">{symbol}{Math.round(stats.total_spent)}</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <p className="text-sm text-gray-500 mb-1">Total Recharged</p>
              <p className="text-3xl font-bold text-gray-900">{symbol}{Math.round(stats.total_recharged)}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mb-6">
            <Button className="bg-green-500 hover:bg-green-600 gap-2" onClick={() => setShowLoadModal(true)} data-testid="load-wallet-btn">
              <Plus size={16} /> Load Credits
            </Button>
            <Button variant="outline" className="gap-2 border-orange-300 text-orange-600 hover:bg-orange-50" onClick={() => setShowCashoutModal(true)} data-testid="cashout-btn">
              <Minus size={16} /> Cash Out
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-xl w-fit">
            {["overview", "orders", "recharges", "wallet_log"].map((tab) => (
              <button key={tab} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`} onClick={() => setActiveTab(tab)} data-testid={`tab-${tab}`}>
                {tab === "overview" ? "Overview" : tab === "orders" ? `Orders (${customerDetail.orders.length})` : tab === "recharges" ? `Recharges (${customerDetail.recharges.length})` : `Wallet Log (${customerDetail.wallet_transactions.length})`}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {activeTab === "overview" && (
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Profile Details</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div><span className="text-gray-500">Name</span><p className="font-medium">{c.name}</p></div>
                    <div><span className="text-gray-500">Email</span><p className="font-medium">{c.email}</p></div>
                    <div><span className="text-gray-500">Phone</span><p className="font-medium">{c.phone || "Not provided"}</p></div>
                    <div><span className="text-gray-500">Role</span><p className="font-medium capitalize">{c.role}</p></div>
                    <div><span className="text-gray-500">Joined</span><p className="font-medium">{formatDate(c.created_at)}</p></div>
                    <div><span className="text-gray-500">Wallet Status</span><p className={`font-medium ${c.wallet_blocked ? "text-red-600" : "text-green-600"}`}>{c.wallet_blocked ? "Blocked" : "Active"}</p></div>
                    <div><span className="text-gray-500">Wallet Balance</span><p className="font-medium">{symbol}{Math.round(c.wallet_balance || 0)}</p></div>
                    <div><span className="text-gray-500">Total Orders</span><p className="font-medium">{stats.order_count}</p></div>
                    <div><span className="text-gray-500">Total Spent</span><p className="font-medium">{symbol}{Math.round(stats.total_spent)}</p></div>
                  </div>
                </div>

                {/* Customer Segments */}
                <CustomerSegmentsEditor
                  userId={c.user_id}
                  value={c.segment_ids || []}
                  onUpdated={(ids) => {
                    setCustomerDetail((prev) => prev ? { ...prev, customer: { ...prev.customer, segment_ids: ids } } : prev);
                  }}
                />
                {c.delivery_addresses && c.delivery_addresses.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><MapPin size={16} className="text-green-600" /> Delivery Addresses</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {c.delivery_addresses.map((addr, idx) => (
                        <div key={idx} className="border border-gray-100 rounded-xl p-3 text-sm bg-gray-50">
                          <p className="font-semibold text-gray-900 mb-1">{addr.label || `Address ${idx + 1}`}</p>
                          <p className="text-gray-600">{addr.full_address || addr.address || "No address text"}</p>
                          {addr.phone && <p className="text-gray-500 text-xs mt-1 flex items-center gap-1"><Phone size={10} /> {addr.phone}</p>}
                          {(addr.lat && addr.lng) && <p className="text-gray-400 text-xs mt-0.5">GPS: {Number(addr.lat).toFixed(4)}, {Number(addr.lng).toFixed(4)}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {customerDetail.orders.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Recent Orders</h3>
                    {customerDetail.orders.slice(0, 5).map((o) => (
                      <div key={o.order_id} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0 text-sm">
                        <div>
                          <span className="font-medium text-gray-900">{o.order_number || o.order_id}</span>
                          <span className="text-gray-400 ml-2">{o.items.length} item{o.items.length > 1 ? "s" : ""}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${o.status === "delivered" ? "bg-green-100 text-green-700" : o.status === "received" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700"}`}>{o.status}</span>
                          <span className="font-medium">{symbol}{Math.round(o.total_amount)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "orders" && (
              <div className="divide-y divide-gray-50">
                {customerDetail.orders.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">No orders yet</div>
                ) : customerDetail.orders.map((o) => (
                  <div key={o.order_id} className="p-4" data-testid={`customer-order-${o.order_id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        {o.order_number && <p className="text-xs font-semibold text-green-600">{o.order_number}</p>}
                        <p className="text-sm text-gray-500">{formatDateTime(o.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${o.status === "delivered" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>{o.status}</span>
                        <p className="font-bold text-gray-900 mt-1">{symbol}{Math.round(o.total_amount)}</p>
                      </div>
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {o.items.map((i, idx) => <span key={idx}>{i.name} x{i.quantity}{idx < o.items.length - 1 ? ", " : ""}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "recharges" && (
              <div className="divide-y divide-gray-50">
                {customerDetail.recharges.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">No recharges yet</div>
                ) : customerDetail.recharges.map((r) => (
                  <div key={r.transaction_id} className="p-4 flex justify-between items-center" data-testid={`customer-recharge-${r.transaction_id}`}>
                    <div>
                      <p className="font-medium text-gray-900">{symbol}{Math.round(r.amount)} paid</p>
                      <p className="text-[10px] text-gray-400">{formatDateTime(r.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600">+{symbol}{Math.round(r.credits)} credits</p>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${r.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>{r.payment_status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "wallet_log" && (
              <div>
                {customerDetail.wallet_transactions.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">No wallet transactions yet</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="text-left p-3 text-gray-500 font-medium text-xs uppercase">Type</th>
                          <th className="text-left p-3 text-gray-500 font-medium text-xs uppercase">Description</th>
                          <th className="text-left p-3 text-gray-500 font-medium text-xs uppercase">Date</th>
                          <th className="text-right p-3 text-gray-500 font-medium text-xs uppercase">Amount</th>
                          <th className="text-right p-3 text-gray-500 font-medium text-xs uppercase">Invoice</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {customerDetail.wallet_transactions.map((t) => (
                          <tr key={t.txn_id} className="hover:bg-gray-50" data-testid={`wallet-txn-${t.txn_id}`}>
                            <td className="p-3">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center ${t.amount >= 0 ? "bg-green-100" : "bg-red-100"}`}>
                                {t.amount >= 0 ? <ArrowDownLeft size={12} className="text-green-600" /> : <ArrowUpRight size={12} className="text-red-600" />}
                              </div>
                            </td>
                            <td className="p-3">
                              <p className="font-medium text-gray-900 capitalize text-sm">{t.type.replace(/_/g, " ")}</p>
                              <p className="text-[10px] text-gray-500">{t.note}</p>
                            </td>
                            <td className="p-3 text-[10px] text-gray-400">{formatDateTime(t.created_at)}</td>
                            <td className="p-3 text-right">
                              <span className={`font-semibold text-sm ${t.amount >= 0 ? "text-green-600" : "text-red-600"}`}>
                                {t.amount >= 0 ? "+" : ""}{symbol}{Math.abs(Math.round(t.amount))}
                              </span>
                            </td>
                            <td className="p-3 text-right">
                              {t.type === "self_recharge" && t.reference_id && (
                                <button onClick={() => window.open(`${BACKEND_URL}/api/wallet/recharge/${t.reference_id}/invoice`, "_blank")} className="text-xs text-green-600 hover:text-green-700 font-medium" data-testid={`admin-txn-invoice-${t.txn_id}`}>
                                  <FileText size={14} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Load Credits Modal */}
          <Dialog open={showLoadModal} onOpenChange={setShowLoadModal}>
            <DialogContent className="bg-white max-w-sm">
              <DialogHeader>
                <DialogTitle>Load Wallet Credits</DialogTitle>
                <DialogDescription>Add credits to {c.name}'s wallet</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Amount</Label>
                  <Input type="number" min="1" value={actionAmount} onChange={(e) => setActionAmount(e.target.value)} placeholder="Enter amount" className="bg-white" data-testid="load-amount" />
                </div>
                <div>
                  <Label>Note (optional)</Label>
                  <Textarea value={actionNote} onChange={(e) => setActionNote(e.target.value)} placeholder="Reason for loading credits" className="bg-white" rows={2} data-testid="load-note" />
                </div>
                <Button className="w-full bg-green-500 hover:bg-green-600" onClick={handleLoadCredits} disabled={actionLoading} data-testid="confirm-load-btn">
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />}
                  Load Credits
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Cashout Modal */}
          <Dialog open={showCashoutModal} onOpenChange={setShowCashoutModal}>
            <DialogContent className="bg-white max-w-sm">
              <DialogHeader>
                <DialogTitle>Cash Out Credits</DialogTitle>
                <DialogDescription>Withdraw from {c.name}'s wallet (Balance: {symbol}{Math.round(c.wallet_balance || 0)})</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Amount</Label>
                  <Input type="number" min="1" max={c.wallet_balance} value={actionAmount} onChange={(e) => setActionAmount(e.target.value)} placeholder="Enter amount" className="bg-white" data-testid="cashout-amount" />
                </div>
                <div>
                  <Label>Note (optional)</Label>
                  <Textarea value={actionNote} onChange={(e) => setActionNote(e.target.value)} placeholder="Reason for cashout" className="bg-white" rows={2} data-testid="cashout-note" />
                </div>
                <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white" onClick={handleCashout} disabled={actionLoading} data-testid="confirm-cashout-btn">
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Minus size={16} className="mr-2" />}
                  Cash Out
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </AdminLayout>
    );
  }

  // Customer List View
  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-customers">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Customer Management</h1>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`${BACKEND_URL}/api/admin/customers/export-pdf`, "_blank")}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="export-customers-pdf-btn"
            >
              <FileText size={14} className="mr-1" /> PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`${BACKEND_URL}/api/admin/customers/export-excel`, "_blank")}
              className="text-green-600 border-green-200 hover:bg-green-50"
              data-testid="export-customers-excel-btn"
            >
              <FileSpreadsheet size={14} className="mr-1" /> Excel
            </Button>
            <span className="text-sm text-gray-500 ml-2">{filtered.length} customer{filtered.length !== 1 ? "s" : ""}</span>
          </div>
        </div>

        {/* Filter */}
        <div className="mb-6 flex items-center gap-3" data-testid="customers-filter-bar">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search by name, email, phone..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-11 bg-white" data-testid="customers-search" />
          </div>
          <Select value={walletFilter} onValueChange={setWalletFilter}>
            <SelectTrigger className="w-40" data-testid="customers-wallet-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Wallets</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
              <SelectItem value="zero">Zero Balance</SelectItem>
            </SelectContent>
          </Select>
          {(searchQuery || walletFilter !== "all") && (
            <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(""); setWalletFilter("all"); }} className="text-gray-500"><X size={14} className="mr-1" /> Clear</Button>
          )}
        </div>

        {/* Customer Table */}
        {loading ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="skeleton h-16 w-full rounded-xl" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No customers found</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left p-4 text-gray-500 font-medium text-sm">Customer</th>
                    <th className="text-left p-4 text-gray-500 font-medium text-sm">Balance</th>
                    <th className="text-left p-4 text-gray-500 font-medium text-sm">Orders</th>
                    <th className="text-left p-4 text-gray-500 font-medium text-sm">Total Spent</th>
                    <th className="text-left p-4 text-gray-500 font-medium text-sm">Status</th>
                    <th className="text-right p-4 text-gray-500 font-medium text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((c) => (
                    <tr key={c.user_id} className="hover:bg-gray-50 cursor-pointer" onClick={() => fetchCustomerDetail(c.user_id)} data-testid={`customer-row-${c.user_id}`}>
                      <td className="p-4">
                        <p className="font-medium text-gray-900">{c.name}</p>
                        <p className="text-[10px] text-gray-500">{c.email}</p>
                        {c.phone && <p className="text-[10px] text-gray-500">{c.phone}</p>}
                      </td>
                      <td className="p-4 font-semibold text-gray-900">{symbol}{Math.round(c.wallet_balance || 0)}</td>
                      <td className="p-4 text-gray-600">{c.order_count || 0}</td>
                      <td className="p-4 text-gray-600">{symbol}{Math.round(c.total_spent || 0)}</td>
                      <td className="p-4">
                        {c.wallet_blocked ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"><Ban size={10} /> Blocked</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleImpersonate(c.user_id, c.email, c.name); }}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            title="View the app as this customer (support session)"
                            data-testid={`impersonate-${c.user_id}`}
                          >
                            <Eye size={12} className="mr-1" /> View as
                          </Button>
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); fetchCustomerDetail(c.user_id); }} data-testid={`view-customer-${c.user_id}`}>
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Impersonation confirmation dialog */}
      <AlertDialog open={!!impConfirm} onOpenChange={(o) => { if (!o) setImpConfirm(null); }}>
        <AlertDialogContent data-testid="impersonate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Start a support session?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-gray-600">
                <p>
                  You&apos;ll browse the app as <span className="font-semibold text-gray-900">{impConfirm?.name || impConfirm?.email}</span>.
                </p>
                <ul className="text-xs space-y-1.5 bg-gray-50 rounded-lg p-3 border border-gray-100 list-disc pl-5">
                  <li>Read-only by default — write actions (orders, redemptions, code regen) are <span className="font-semibold text-red-700">blocked</span>.</li>
                  <li>You can toggle <span className="font-semibold">Allow edits</span> on the banner if you need to fix something on their behalf.</li>
                  <li>The session expires in 30 minutes. Every action is audited.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="impersonate-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmImpersonate} className="bg-red-600 hover:bg-red-700" data-testid="impersonate-confirm-btn">
              Start session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
